/**
 * The statistics panel behind every counter in the top bar.
 *
 * It exists because the HUD strip can only answer "how much"; the questions a
 * player actually acts on are "where does that rate come from", "why is it
 * lower than I expect" and "when does my store overflow". Every number here is
 * derived from the same engine reports the bar uses, so the breakdown can never
 * disagree with the headline figure.
 */

import {
  ResourceStats,
  formatAmount,
  type ResourceSource,
} from '@spacegame/ui';
import {
  BUILDINGS,
  BASE_INCOME_SILICATE,
  BASE_INCOME_TITAN,
  MODIFIER_LABELS,
  activeEffects,
  buildingLevel,
  isotopeTempFactor,
  mineProduction,
  productionReport,
  storageReport,
  talentEffects,
  type GameState,
} from '@spacegame/engine';
import { useGame } from '../game/GameProvider';

/** Resources the top bar can open a panel for. */
export type StatsResource = 'titan' | 'silicate' | 'isotope' | 'power' | 'aether' | 'credit' | 'chronos';

const MINE_OF = {
  titan: 'titanSmelter',
  silicate: 'silicateRefinery',
  isotope: 'isotopeCondenser',
} as const;

const BASE_INCOME = {
  titan: BASE_INCOME_TITAN,
  silicate: BASE_INCOME_SILICATE,
  isotope: 0,
} as const;

/**
 * Break a mine's rate into the parts the player can influence.
 *
 * The mine line already carries the energy factor and the talent multiplier,
 * because that is the number that actually lands in the store — showing an
 * unthrottled figure and a separate "−x % energy" line reads as a promise the
 * game then breaks.
 */
function mineSources(game: GameState, resource: 'titan' | 'silicate' | 'isotope'): ResourceSource[] {
  const key = MINE_OF[resource];
  const level = buildingLevel(game, key);
  const report = productionReport(game, game.lastTick);
  const talents = talentEffects(game.talents);
  const damaged = game.planets[0]!.damaged.includes(key);

  const sources: ResourceSource[] = [];

  if (BASE_INCOME[resource] > 0) {
    sources.push({
      label: 'Grundförderung',
      perHour: BASE_INCOME[resource],
      detail: 'Unabhängig von Energie und Gebäudestufe',
    });
  }

  const raw = mineProduction(BUILDINGS[key].production!.basePerHour, level) *
    report.energy.factor *
    talents.mineBonus *
    (resource === 'isotope' ? isotopeTempFactor(game.planets[0]!.temperature) : 1) *
    (resource === 'silicate' && game.planets[0]!.crater ? game.planets[0]!.crater.factor : 1);

  sources.push({
    label: BUILDINGS[key].label,
    perHour: damaged ? 0 : raw,
    detail: damaged
      ? 'Beschädigt — produziert nichts bis zur Reparatur'
      : [
          `Stufe ${level}`,
          report.energy.factor < 1 ? `Energie ${Math.round(report.energy.factor * 100)} %` : null,
          resource === 'isotope'
            ? `Temperatur ×${isotopeTempFactor(game.planets[0]!.temperature).toFixed(2)}`
            : null,
          resource === 'silicate' && game.planets[0]!.crater ? 'Krater ×2' : null,
        ]
          .filter(Boolean)
          .join(' · '),
  });

  return sources;
}

export function ResourceStatsPanel({
  resource,
  onClose,
}: {
  resource: StatsResource | null;
  onClose: () => void;
}) {
  const { game, clock } = useGame();
  if (!resource) return null;

  const now = clock();
  const report = productionReport(game, now);
  const caps = storageReport(game, now);
  const effects = activeEffects(game, now);

  // Every panel lists the buffs currently bending its numbers — otherwise a
  // rate that changed for a good reason looks like a bug.
  const buffs = effects.length > 0 ? (
    <div className="rounded-control border border-silicate/30 bg-silicate/[0.06] px-2.5 py-2">
      <span className="font-display text-label uppercase text-silicate-bright">Aktive Boni</span>
      <ul className="mt-1 space-y-0.5">
        {effects.map((effect) => (
          <li key={effect.id} className="text-xs text-content">
            {effect.label}: {MODIFIER_LABELS[effect.category]} {effect.percent >= 0 ? '+' : ''}
            {effect.percent} %
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  if (resource === 'titan' || resource === 'silicate' || resource === 'isotope') {
    return (
      <ResourceStats
        open
        onClose={onClose}
        resource={resource}
        amount={game.resources[resource]}
        ratePerHour={report.perHour[resource]}
        capacity={caps[resource]}
        sources={mineSources(game, resource)}
        footnotes={buffs}
      />
    );
  }

  if (resource === 'power') {
    return (
      <ResourceStats
        open
        onClose={onClose}
        resource="power"
        amount={report.energy.produced - report.energy.consumed}
        sources={[
          {
            label: BUILDINGS.solarPlant.label,
            perHour: report.energy.produced,
            detail: `Stufe ${buildingLevel(game, 'solarPlant')}`,
          },
          {
            label: 'Verbrauch aller Minen',
            perHour: -report.energy.consumed,
            detail:
              report.energy.factor < 1
                ? `Defizit — Minen laufen mit ${Math.round(report.energy.factor * 100)} %`
                : 'Alle Minen laufen mit 100 %',
          },
        ]}
        footnotes={
          <>
            <p className="text-xs leading-relaxed text-content-muted">
              Energie wird nicht gelagert. Reicht die Leistung nicht, fördern alle Minen
              anteilig langsamer — der Energiebedarf wächst schneller als die Förderung, das ist
              die eigentliche Bremse im späten Spiel.
            </p>
            {buffs}
          </>
        }
      />
    );
  }

  // Currencies have no production line; the panel becomes a plain readout with
  // the one sentence that explains where the currency comes from.
  const CURRENCY_NOTE: Record<'aether' | 'credit' | 'chronos', string> = {
    aether: 'Aether-Masse stammt aus Endgame-Anomalien und Events.',
    credit: 'Galaktische Credits kommen aus dem Händler-Portal und dem Casino.',
    chronos: 'Chronos-Kristalle sind Premium-Währung. Ausgaben werden immer vorher bestätigt.',
  };

  return (
    <ResourceStats
      open
      onClose={onClose}
      resource={resource}
      amount={game.resources[resource]}
      footnotes={
        <>
          <p className="text-xs leading-relaxed text-content-muted">{CURRENCY_NOTE[resource]}</p>
          {resource === 'credit' ? (
            <p className="text-xs leading-relaxed text-content-muted">
              Heute im Casino eingesetzt: {formatAmount(game.casino.stakedToday)} Credits.
            </p>
          ) : null}
          {buffs}
        </>
      }
    />
  );
}
